import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import {
  approveProposal,
  declineProposal,
  gscSignals,
  listRefreshCandidates,
  runProposals,
} from "@/lib/content-refresh.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/innehall")({
  head: () => ({
    meta: [
      { title: "Innehållsförslag — AK Husbil & Fritid" },
      {
        name: "description",
        content: "Internt verktyg: förslag på innehållsförbättringar som väntar på granskning.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: RefreshPage,
});

const statusStyle: Record<string, string> = {
  proposed: "bg-primary/15 text-primary border-primary/30",
  updated: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-400",
  skipped: "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400",
  rejected: "bg-muted text-muted-foreground border-border",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  pending: "bg-muted text-muted-foreground border-border",
};

function RefreshPage() {
  const diagnose = useServerFn(listRefreshCandidates);
  const propose = useServerFn(runProposals);
  const approve = useServerFn(approveProposal);
  const decline = useServerFn(declineProposal);
  const signals = useServerFn(gscSignals);
  const [open, setOpen] = useState<string | null>(null);

  const diagnosis = useMutation({ mutationFn: () => diagnose({ data: { limit: 12 } }) });
  const gsc = useMutation({ mutationFn: (siteUrl?: string) => signals({ data: { siteUrl } }) });
  const run = useMutation({
    mutationFn: (max: number) => propose({ data: { max } }),
    onSuccess: () => diagnosis.mutate(),
  });
  const accept = useMutation({
    mutationFn: (id: string) => approve({ data: { id } }),
    onSuccess: () => diagnosis.mutate(),
  });
  const reject = useMutation({
    mutationFn: (id: string) => decline({ data: { id } }),
    onSuccess: () => diagnosis.mutate(),
  });

  const error = (diagnosis.error ?? run.error ?? accept.error ?? reject.error) as Error | null;

  const gscData = gsc.data;
  const pages = gscData?.status === "ok" ? (gscData.pages ?? []) : [];
  const signalFor = (targetType: string, slug: string) => {
    const path = `${targetType === "blog_post" ? "/blogg/" : "/kunskap/"}${slug}`.replace(/\/+$/, "");
    return pages.find((p) => p.path.replace(/\/+$/, "") === path) ?? null;
  };


  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <h1 className="text-2xl font-bold tracking-tight">Innehållsförslag</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Motorn letar bara efter <strong>objektiva</strong> brister: för kort text, saknad eller
        felaktig meta description, ingen rubrikstruktur, inga länkar. Positioner i Google används
        aldrig som skäl att skriva om en sida — en tappad placering kan bero på algoritmändringar,
        konkurrenter eller ändrad sökintention. Nattjobbet lägger förslag i kö; ingenting publiceras
        förrän du godkänner det här.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={() => diagnosis.mutate()} disabled={diagnosis.isPending} className="min-h-11">
          {diagnosis.isPending ? "Analyserar…" : "Analysera sajten"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => run.mutate(1)}
          disabled={run.isPending}
          className="min-h-11"
        >
          {run.isPending ? "Tar fram förslag…" : "Ta fram nytt förslag"}
        </Button>
        <Button
          variant="outline"
          onClick={() => gsc.mutate(undefined)}
          disabled={gsc.isPending}
          className="min-h-11"
        >
          {gsc.isPending ? "Hämtar Search Console…" : "Visa Search Console-siffror"}
        </Button>
      </div>

      {gscData && gscData.status !== "ok" && (
        <p className="mt-3 text-sm text-muted-foreground">
          {gscData.status === "selection_required"
            ? `Flera Search Console-egenskaper matchar: ${gscData.candidates?.join(", ")}`
            : (gscData.message ?? "Search Console-data är inte tillgänglig.")}
        </p>
      )}
      {gscData?.status === "ok" && (
        <p className="mt-3 text-xs text-muted-foreground">
          Search Console, senaste 28 dagarna ({gscData.siteUrl}). Endast information — siffrorna
          påverkar aldrig vilka sidor motorn föreslår.
        </p>
      )}

      {error && <p className="mt-4 text-sm text-destructive">{error.message}</p>}

      {diagnosis.data && (
        <>
          <h2 className="mt-10 text-lg font-semibold">
            Väntar på granskning ({diagnosis.data.pending.length})
          </h2>
          <ul className="mt-3 space-y-3">
            {diagnosis.data.pending.map((p) => (
              <li key={p.id} className="rounded-lg border border-border bg-card p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${statusStyle.proposed}`}>
                    förslag
                  </span>
                  <span className="font-medium">{p.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {p.target_type === "blog_post" ? "/blogg/" : "/kunskap/"}
                    {p.slug}
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {p.words_before} → {p.words_after} ord · skäl: {p.reason}
                  {p.notes ? ` · ${p.notes}` : ""}
                </p>
                {p.proposed_meta && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Ny meta description: {p.proposed_meta}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="min-h-11"
                    onClick={() => accept.mutate(p.id)}
                    disabled={accept.isPending}
                  >
                    Godkänn och publicera
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-11"
                    onClick={() => reject.mutate(p.id)}
                    disabled={reject.isPending}
                  >
                    Avslå
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-11"
                    onClick={() => setOpen(open === p.id ? null : p.id)}
                  >
                    {open === p.id ? "Dölj utkast" : "Visa utkast"}
                  </Button>
                </div>
                {open === p.id && (
                  <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">
                    {p.preview}
                  </pre>
                )}
              </li>
            ))}
            {diagnosis.data.pending.length === 0 && (
              <li className="text-sm text-muted-foreground">Inga förslag i kön just nu.</li>
            )}
          </ul>

          <h2 className="mt-10 text-lg font-semibold">Sidor med objektiva brister</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Poäng</th>
                  <th className="py-2 pr-3">Sida</th>
                  <th className="py-2 pr-3">Ord</th>
                  <th className="py-2 pr-3">Search Console (info)</th>
                  <th className="py-2">Brister</th>
                </tr>
              </thead>
              <tbody>
                {diagnosis.data.candidates.map((c) => {
                  const s = signalFor(c.targetType, c.slug);
                  return (
                  <tr key={`${c.targetType}-${c.slug}`} className="border-t border-border align-top">
                    <td className="py-2 pr-3 font-mono">{c.score}</td>
                    <td className="py-2 pr-3">
                      <span className="block font-medium">{c.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.targetType === "blog_post" ? "/blogg/" : "/kunskap/"}
                        {c.slug}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{c.words}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {gscData?.status !== "ok"
                        ? "—"
                        : s
                          ? `${Math.round(s.clicks)} klick · ${Math.round(s.impressions)} visningar · pos ${s.position.toFixed(1)}`
                          : "ingen data"}
                    </td>
                    <td className="py-2 text-muted-foreground">{c.reasons.join("; ")}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h2 className="mt-10 text-lg font-semibold">Historik</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {diagnosis.data.log.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
                <span className={`rounded-full border px-2 py-0.5 text-xs ${statusStyle[l.status] ?? ""}`}>
                  {l.status}
                </span>
                <span className="font-medium">{l.slug}</span>
                <span className="text-muted-foreground">
                  {l.words_before ?? "—"} → {l.words_after ?? "—"} ord
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(l.created_at).toLocaleString("sv-SE")}
                </span>
                {l.notes && <span className="w-full text-xs text-muted-foreground">{l.notes}</span>}
              </li>
            ))}
            {diagnosis.data.log.length === 0 && (
              <li className="text-muted-foreground">Inga körningar loggade ännu.</li>
            )}
          </ul>
        </>
      )}
    </main>
  );
}
